// Pieces of the staff leads dashboard with no JSX: labels, request params,
// colours, the ET formatters and the numbers derived from two responses. In
// a `.js` file so `react-refresh/only-export-components` stays quiet, same as
// components/partner-dashboard/model.js.
//
// Shape of what /admin/leads/dashboard returns: StaffDashboardOut in the
// backend's leads/admin_api.py, computed by leads/staff_dashboard.py. Every
// code below (reasons, outcomes, statuses) belongs to the backend; this file
// only decides how a code reads on screen.

import { isCustomRange, parseUrlRange, rangeParams, rangeSearch } from '../../utils/dateRange'

// A range is a preset id below or a custom `{ from, to }` (utils/dateRange).
export const DEFAULT_RANGE = 'today'

// `title` is the page heading for that range.
export const RANGE_PRESETS = [
  { id: 'today', label: 'Today', title: 'Today’s sales' },
  { id: '7d', label: '7 days', title: 'Last 7 days' },
  { id: '30d', label: '30 days', title: 'Last 30 days' },
  { id: 'ytd', label: 'YTD', title: 'Year to date' },
]

export const rangeTitle = (range) => (
  isCustomRange(range)
    ? 'Custom range'
    : RANGE_PRESETS.find((p) => p.id === range)?.title || RANGE_PRESETS[0].title
)

// Only today changes minute to minute; the longer ranges refresh on demand.
export const AUTO_REFRESH_MS = 60_000
export const autoRefreshes = (range) => range === 'today'

export const PAGE_SIZE = 50

// EffectiveStatus in leads/staff_dashboard.py. `new` (routing not finished)
// has no tab: it shows up in the funnel only, under its real name.
export const TABS = [
  { id: 'sold', label: 'Sold' },
  { id: 'accepted_not_sold', label: 'Accepted, not sold' },
  { id: 'rejected', label: 'Rejected' },
]
export const DEFAULT_TAB = 'sold'

// ── labels ──────────────────────────────────────────────────────────────

// Lead-level reasons a lead did not sell: Lead.no_sale_reason values,
// `unrecorded` for an empty column and `sale_not_counted` for a SOLD row the
// dashboard does not count. The full list lives in model.test.js, spelled
// out, so a code nobody taught this screen fails a test.
export const REASON_LABELS = {
  no_eligible_buyers: 'No eligible buyers',
  all_declined: 'All buyers declined',
  bids_below_floor: 'Bids below floor',
  post_failed: 'Post failed',
  buyer_errors: 'Buyer errors',
  routing_error: 'Routing error',
  sale_not_counted: 'Sale not counted',
  unrecorded: 'Not recorded',
}

// staff_dashboard.OUTCOMES. Nothing else exists: no pending, duplicate or
// missing field, because routing records none of them.
export const OUTCOME_LABELS = {
  won: 'Won',
  bid: 'Bid',
  declined: 'Declined',
  timeout: 'Timeout',
  error: 'Error',
  below_floor: 'Below floor',
  post_failed: 'Post failed',
  excluded: 'Excluded',
}

// An unknown code is still never shown as bare snake_case.
const humanize = (code) => String(code).replace(/_/g, ' ')

export function reasonLabel(code) {
  if (!code) return { label: '—', known: false }
  return REASON_LABELS[code]
    ? { label: REASON_LABELS[code], known: true }
    : { label: `Other (${humanize(code)})`, known: false }
}

export function outcomeLabel(outcome) {
  return OUTCOME_LABELS[outcome]
    ? { label: OUTCOME_LABELS[outcome], known: true }
    : { label: `Other (${humanize(outcome)})`, known: false }
}

const TONE = {
  green: { fg: 'var(--green)', bg: 'var(--green-bg)' },
  amber: { fg: 'var(--amber)', bg: 'var(--amber-bg)' },
  red: { fg: 'var(--red)', bg: 'var(--red-bg)' },
  blue: { fg: 'var(--blue)', bg: 'var(--blue-bg)' },
  purple: { fg: 'var(--purple)', bg: 'var(--purple-bg)' },
  muted: { fg: 'var(--text-secondary)', bg: 'var(--bg-raised)' },
}

// Amber: nobody wanted it. Red: something broke on the way. Purple: the
// sale happened on the row but does not count. Muted: nothing to go on.
const REASON_TONE = {
  no_eligible_buyers: 'amber',
  all_declined: 'amber',
  bids_below_floor: 'amber',
  post_failed: 'red',
  buyer_errors: 'red',
  routing_error: 'red',
  sale_not_counted: 'purple',
  unrecorded: 'muted',
}

const OUTCOME_TONE = {
  won: 'green',
  bid: 'blue',
  declined: 'muted',
  timeout: 'amber',
  error: 'red',
  below_floor: 'amber',
  post_failed: 'red',
  excluded: 'muted',
}

export const reasonTone = (code) => TONE[REASON_TONE[code] || 'muted']
export const outcomeTone = (outcome) => TONE[OUTCOME_TONE[outcome] || 'muted']

// ── buyer colours ───────────────────────────────────────────────────────

export const BUYER_SLOTS = 6
export const OTHER_BUYER_COLOR = 'var(--series-other)'

// A buyer's colour comes from its position in `filters.buyers` (the backend
// sorts it by name), never from a name in this code. That list does not
// change with the range or the chips, so a buyer keeps its colour while the
// rest of the screen moves. Past BUYER_SLOTS, buyers share the neutral
// colour rather than reusing a hue - the name beside it carries identity.
//
// Rows outside `filters` carry only a label (a lead's winner, a buyer
// response), so lookup works by id and by label. A hidden buyer reaches the
// screen as "Other buyer", which is in neither, and gets the neutral colour.
export function buyerPalette(buyers = []) {
  const byId = new Map()
  const byLabel = new Map()
  buyers.forEach((b, i) => {
    const color = i < BUYER_SLOTS ? `var(--series-${i + 1})` : OTHER_BUYER_COLOR
    byId.set(String(b.id), color)
    byLabel.set(b.label, color)
  })
  return {
    forId: (id) => byId.get(String(id)) || OTHER_BUYER_COLOR,
    forLabel: (label) => byLabel.get(label) || OTHER_BUYER_COLOR,
  }
}

// ── request params ──────────────────────────────────────────────────────

const csv = (values) => (values && values.length ? values.join(',') : undefined)

// Strips undefined so a params object compares, logs and serializes as
// exactly what is sent.
const clean = (obj) => Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined && v !== null && v !== ''))

// KPIs, funnel and aggregates. Never carries status or reason: those filter
// the whole queryset server-side, so they would move the KPIs with the tab.
export const overviewParams = ({ range, origins, buyers }) =>
  clean({ ...rangeParams(range), origins: csv(origins), buyers: csv(buyers), limit: 1 })

// The same view without the buyer filter - only needed when one is set. A
// buyer filter matches on winning_buyer, so it empties accepted and
// rejected; this is where their counts and reasons come from instead.
export const baseParams = ({ range, origins }) => clean({ ...rangeParams(range), origins: csv(origins), limit: 1 })

// The table under the tabs. `buyers` only on Sold: accepted and rejected
// leads have no winning buyer, so the filter would empty them.
export const listParams = ({ range, origins, buyers, tab, reason, page }) =>
  clean({
    ...rangeParams(range),
    origins: csv(origins),
    buyers: tab === 'sold' ? csv(buyers) : undefined,
    status: tab,
    reason: tab === 'sold' ? undefined : reason,
    page,
    limit: PAGE_SIZE,
  })

// The export is the table, every page of it.
export const exportParams = (state) => {
  const { page: _page, limit: _limit, ...rest } = listParams(state)
  return rest
}

// ── filters in the URL ──────────────────────────────────────────────────

// The screen's filters live in the query string, with the same keys the
// backend writes into the Home's alert links (leads/home_overview.py
// _leads_link: range or from/to, buyers, origins, status), so a link lands
// filtered and a filtered view can be copied and shared.

const listFrom = (value) => (value ? String(value).split(',').map((v) => v.trim()).filter(Boolean) : [])

// Range and tab are checked against what this screen knows. Buyers and
// origins come out as candidates only: they are not applied until checked
// against `filters` by allowedFilters().
export function parseUrlFilters(searchParams) {
  const status = searchParams.get('status')
  return {
    range: parseUrlRange(searchParams, RANGE_PRESETS, DEFAULT_RANGE),
    tab: TABS.some((t) => t.id === status) ? status : DEFAULT_TAB,
    buyers: listFrom(searchParams.get('buyers')),
    origins: listFrom(searchParams.get('origins')),
  }
}

// Only what filter_options offers survives. A hidden buyer is never in
// `filters.buyers`, so its id is dropped here - never sent in a request,
// never a chip, never written back into the URL.
export function allowedFilters({ buyers = [], origins = [] }, filters) {
  const buyerIds = new Set((filters?.buyers || []).map((b) => String(b.id)))
  const originNames = new Set(filters?.origins || [])
  return {
    buyers: [...new Set(buyers.map(String))].filter((id) => buyerIds.has(id)),
    origins: [...new Set(origins)].filter((o) => originNames.has(o)),
  }
}

// The query string for a state, defaults left out.
export const filtersToSearch = ({ range, origins, buyers, tab }) =>
  clean({
    ...rangeSearch(range, DEFAULT_RANGE),
    origins: csv(origins),
    buyers: csv(buyers),
    status: tab !== DEFAULT_TAB ? tab : undefined,
  })

// ── numbers from two responses ──────────────────────────────────────────

const n = (v) => Number(v || 0)

// Everything the top half and the tab bar show, derived from the overview
// (with the buyer filter) and the base (without it). With no buyer selected
// the two are the same response.
//
// The rule with a buyer selected: sales and money are that buyer's, every
// count of leads that did not sell covers all leads, and nothing on screen
// may contradict anything else. The funnel's segments therefore add up to
// the base `received`, splitting sold into "selected buyer" and "other
// buyers".
export function deriveView(overview, base, buyerFiltered) {
  const ov = overview?.kpis || {}
  const bk = (buyerFiltered ? base?.kpis : ov) || {}
  const received = n(bk.received)
  const sold = n(ov.sold)

  const funnel = buyerFiltered
    ? [
        { id: 'sold', label: 'Sold to selected buyer', value: sold, color: 'var(--green)' },
        { id: 'sold_other', label: 'Sold to other buyers', value: Math.max(0, n(bk.sold) - sold), color: 'var(--funnel-sold-other)' },
        { id: 'accepted_not_sold', label: 'Accepted, not sold', value: n(bk.accepted_not_sold), color: 'var(--amber)' },
        { id: 'rejected', label: 'Rejected', value: n(bk.rejected), color: 'var(--red)' },
        { id: 'new', label: 'New', value: n(bk.new), color: 'var(--funnel-new)' },
      ]
    : [
        { id: 'sold', label: 'Sold', value: sold, color: 'var(--green)' },
        { id: 'accepted_not_sold', label: 'Accepted, not sold', value: n(ov.accepted_not_sold), color: 'var(--amber)' },
        { id: 'rejected', label: 'Rejected', value: n(ov.rejected), color: 'var(--red)' },
        { id: 'new', label: 'New', value: n(ov.new), color: 'var(--funnel-new)' },
      ]

  return {
    kpis: {
      sold,
      received,
      soldPct: received > 0 ? (sold / received) * 100 : 0,
      revenue: n(ov.revenue),
      avgPerSale: sold > 0 ? n(ov.revenue) / sold : 0,
      ourShare: n(ov.our_share),
      partnerShare: n(ov.partner_share),
      unpricedSold: n(ov.unpriced_sold),
    },
    // New is shown only when there is some.
    funnel: funnel.filter((s) => s.id !== 'new' || s.value > 0),
    funnelTotal: received,
    tabCounts: {
      sold,
      accepted_not_sold: n(bk.accepted_not_sold),
      rejected: n(bk.rejected),
    },
    reasons: (buyerFiltered ? base?.reasons : overview?.reasons) || {},
  }
}

// Reason cards for one tab, largest first, each with its share of the tab.
export function reasonRows(reasons = {}) {
  const entries = Object.entries(reasons).filter(([, c]) => n(c) > 0)
  const total = entries.reduce((s, [, c]) => s + n(c), 0)
  return entries
    .sort((a, b) => n(b[1]) - n(a[1]))
    .map(([code, count]) => ({ code, count: n(count), pct: total > 0 ? (n(count) / total) * 100 : 0 }))
}

// ── formatting (America/New_York, from response.timezone) ───────────────

export const FALLBACK_TIMEZONE = 'America/New_York'

const formatters = new Map()
function formatter(timeZone, options) {
  const key = `${timeZone}|${JSON.stringify(options)}`
  if (!formatters.has(key)) formatters.set(key, new Intl.DateTimeFormat('en-US', { timeZone, ...options }))
  return formatters.get(key)
}

// "14:03" - the clock the header and the buyer card speak.
export function fmtClock(value, timeZone = FALLBACK_TIMEZONE) {
  const d = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  return formatter(timeZone, { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(d)
}

// "Oct 9, 14:03" - a lead's creation time. The day is always there, since
// most ranges span more than one.
export function fmtLeadTime(value, timeZone = FALLBACK_TIMEZONE) {
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  return formatter(timeZone, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(d)
}

// by_hour's hour of day (0-23) as an axis label: "12a", "1p".
export function fmtHour(hour) {
  const h = Number(hour)
  const suffix = h < 12 ? 'a' : 'p'
  return `${h % 12 === 0 ? 12 : h % 12}${suffix}`
}

// Compact money for bar labels: "$1.2k", "$85".
export function compactMoney(v) {
  const x = n(v)
  if (x === 0) return '$0'
  if (Math.abs(x) >= 1000) return `$${(x / 1000).toLocaleString('en-US', { maximumFractionDigits: 1 })}k`
  return `$${x.toLocaleString('en-US', { maximumFractionDigits: 0 })}`
}

export const count = (v) => n(v).toLocaleString('en-US')
export const pct = (v) => `${n(v).toLocaleString('en-US', { maximumFractionDigits: 1 })}%`

// by_buyer_as_of is when the breakdown was computed. For ranges over 7 days
// the backend serves it from a 5-minute cache, so it can be older than the
// request that returned it; a minute of slack keeps a live answer from
// being called stale.
export function buyerDataStale(asOf, requestedAt) {
  if (!asOf || !requestedAt) return false
  const t = new Date(asOf).getTime()
  return !Number.isNaN(t) && requestedAt.getTime() - t > 60_000
}

export const STAFF_LEADS_RESPONSIVE_CSS = `
/* No backticks in these comments: this lives inside a JS template literal. */
.sl-kpis { display: grid; gap: 14px; grid-template-columns: repeat(4, minmax(0, 1fr)); }
.sl-split { display: grid; gap: 16px; grid-template-columns: repeat(2, minmax(0, 1fr)); }
.sl-reasons { display: grid; gap: 10px; grid-template-columns: repeat(auto-fill, minmax(190px, 1fr)); }
.sl-table-scroll { overflow-x: auto; -webkit-overflow-scrolling: touch; }
.sl-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.sl-table th { text-align: left; font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: .04em; color: var(--text-muted); padding: 10px 14px; border-bottom: 1px solid var(--border); white-space: nowrap; }
.sl-table td { padding: 10px 14px; border-bottom: 1px solid var(--border); color: var(--text-primary); vertical-align: top; }
.sl-table tr:last-child td { border-bottom: none; }
.sl-table .sl-num { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }
.sl-chip { display: inline-flex; align-items: center; gap: 6px; padding: 5px 10px; border-radius: 999px; font-size: 12px; font-weight: 600; cursor: pointer; border: 1px solid var(--border); background: transparent; color: var(--text-secondary); transition: all .12s; }
.sl-chip[aria-pressed="true"] { background: var(--accent-soft); color: var(--accent); border-color: var(--accent); }
.sl-chip:focus-visible, .sl-reason:focus-visible, .sl-tab:focus-visible { outline: 2px solid var(--accent); outline-offset: 2px; }
.sl-reason { text-align: left; cursor: pointer; border: 1px solid var(--border); background: var(--bg-surface); border-radius: var(--radius); padding: 12px 14px; color: var(--text-primary); transition: border-color .12s; }
.sl-reason[aria-pressed="true"] { border-color: var(--accent); box-shadow: 0 0 0 1px var(--accent); }
.sl-tab { background: none; border: none; border-bottom: 2px solid transparent; padding: 10px 4px; margin-right: 18px; font-size: 13px; font-weight: 600; color: var(--text-muted); cursor: pointer; white-space: nowrap; }
.sl-tab[aria-selected="true"] { color: var(--text-primary); border-bottom-color: var(--accent); }
.sl-hour-bars { display: grid; grid-template-columns: repeat(24, minmax(0, 1fr)); gap: 2px; align-items: end; height: 170px; }
.sl-spin { animation: sl-spin 0.9s linear infinite; }
@keyframes sl-spin { to { transform: rotate(360deg); } }
.sl-skeleton { background: var(--bg-raised); border-radius: 6px; animation: sl-pulse 1.2s ease-in-out infinite; }
@keyframes sl-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.45; } }
@media (prefers-reduced-motion: reduce) { .sl-spin, .sl-skeleton { animation: none; } }
@media (max-width: 1100px) {
  .sl-kpis { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
  .sl-split { grid-template-columns: 1fr !important; }
}
@media (max-width: 768px) {
  .sl-header { flex-direction: column !important; align-items: stretch !important; }
  .sl-header-actions { justify-content: flex-start !important; flex-wrap: wrap; }
  .sl-tabs { overflow-x: auto; }
  .sl-tab { margin-right: 12px; }
  .sl-search input { font-size: 16px !important; }
}
@media (max-width: 480px) {
  .sl-kpis { grid-template-columns: 1fr !important; }
}
`
